const adminOrganization = {
  actions: {
    addMember: 'Add member',
    batchDisable: 'Disable',
    cancel: 'Cancel',
    confirm: 'Confirm',
    createMember: 'New',
    createDepartment: 'New department',
    delete: 'Delete',
    deleteDepartment: 'Delete department',
    deleteMember: 'Remove',
    disable: 'Disable',
    edit: 'Edit',
    enable: 'Enable',
    manage: 'Manage',
    retry: 'Retry',
    resetPassword: 'Reset password',
    save: 'Save',
    settings: 'Settings',
    view: 'View',
    viewAttributes: 'View synced details',
  },
  common: {
    allDepartments: 'All departments',
    allStatuses: 'All statuses',
    loading: 'Loading',
    noDepartment: 'Unassigned',
    selectedCount: '{{count}} selected',
    selectCompany: 'Select organization',
  },
  errors: {
    batchStatusPartial: 'Some member statuses failed to update',
    deleteDefaultDepartment: 'The default unassigned department cannot be deleted.',
    deleteDepartmentWithChildren: 'Handle child departments before deleting this department.',
    deleteDepartmentWithMembers: 'Remove or transfer members before deleting this department.',
    deleteRootDepartment: 'The root organization cannot be deleted.',
    loadDescription: 'Organization and member data failed to load. Please try again later.',
    loadTitle: 'Unable to load department data',
    noCompany: 'No organization is available',
    removeMemberPartial:
      'Some members could not be removed. Successfully removed members were refreshed.',
    submitFailed: 'Operation failed. Please try again later.',
    syncReadOnly:
      'Organization sync is running. Manual department and member changes are temporarily disabled; reads remain available.',
  },
  memberAccounts: {
    attributesDrawer: {
      copied: 'Copied',
      copy: 'Copy JSON',
      description:
        'These details are synced from identity sources such as enterprise directories and school systems. They supplement the member profile and help diagnose sync results. Only fields explicitly configured in an IdP Scope are shared with external systems.',
      empty: 'No synced details are available for this member',
      errorDescription: 'Unable to load this member’s synced details. Please try again later.',
      errorTitle: 'Unable to load synced details',
      fieldCount: '{{count}} fields synced',
      formattedJson: 'Formatted raw sync data',
      loading: 'Loading synced details',
      rawJson: 'Raw sync data',
      title: 'Synced details / {{name}}',
    },
    columnSelector: {
      label: 'Choose member account columns',
      trigger: 'Columns',
    },
    description: 'Manage member accounts, departments, and status.',
    drawer: {
      department: 'Department',
      email: 'Email account',
      lastActiveAt: 'Last active',
      name: 'Member name',
      namePlaceholder: 'Enter member name',
      role: 'Role',
      status: 'Status',
      title: 'Manage member account',
      titleWithMember: 'Manage member account / {{name}}',
      userId: 'User ID',
      validation: {
        nameRequired: 'Enter member name',
      },
    },
    createModal: {
      close: 'Close',
      confirm: 'Create member',
      email: 'Email address',
      emailPlaceholder: 'name@company.com',
      mustChangePassword: 'Require password change on first sign-in',
      name: 'Member name',
      namePlaceholder: 'Enter member name',
      title: 'New member account',
      validation: {
        emailInvalid: 'Please enter a valid email',
        emailRequired: 'Enter email',
        nameRequired: 'Enter member name',
      },
    },
    resetPasswordModal: {
      close: 'Close',
      confirm: 'Confirm reset',
      description: 'Reset the password for {{email}}?',
      impact:
        'Creates a temporary password; old sessions end, next sign-in requires a password change.',
      title: 'Confirm password reset',
    },
    empty: 'No member accounts',
    searchLabel: 'Search member name',
    searchPlaceholder: 'Search member name',
    role: {
      admin: 'Administrator',
      unknown: 'Not set',
      user: 'Member',
    },
    status: {
      active: 'Active',
      banned: 'Disabled',
    },
    sort: {
      createdAtAsc: 'Sort by created time ascending',
      createdAtDesc: 'Sort by created time descending',
      lastActiveAtAsc: 'Sort by last active time ascending',
      lastActiveAtDesc: 'Sort by last active time descending',
    },
    table: {
      actions: 'Actions',
      createdAt: 'Created',
      department: 'Department',
      email: 'Email account',
      lastActiveAt: 'Last active',
      name: 'Member name',
      role: 'Role',
      status: 'Status',
    },
    title: 'Member accounts',
    workspaceLabel: 'Member accounts workspace',
  },
  organization: {
    addMemberDialog: {
      empty: 'No members available',
      searchLabel: 'Search members',
      searchPlaceholder: 'Search member name or email',
      subtitle: 'Add members to {{name}}.',
      title: 'Add member',
    },
    createDepartmentDialog: {
      nameLabel: 'Department name',
      namePlaceholder: 'Enter department name',
      subtitle: 'The new department will be created under {{name}}.',
      title: 'New department',
    },
    deleteDepartmentDialog: {
      subtitle: 'This cannot be undone: {{name}}',
      title: 'Delete department',
    },
    description: 'Manage department hierarchy and member assignment.',
    emptyMemberAddAction: 'Click to add members',
    emptyMemberSearchResults: 'No matching members',
    emptyMembers: 'No members in this organization',
    memberSearchLabel: 'Search member name or email',
    memberSearchPlaceholder: 'Enter member name or email',
    orgSearchLabel: 'Search organization',
    orgSearchPlaceholder: 'Enter organization',
    renameDepartmentDialog: {
      nameLabel: 'Department name',
      namePlaceholder: 'Enter department name',
      title: 'Edit department',
    },
    settingsDrawer: {
      cronExpression: 'Sync cron',
      lastRunCompletedAt: 'Last completed',
      lastRunMessage: 'Last message',
      lastRunStatus: 'Last status',
      lockActive: 'Syncing',
      lockCompanyId: 'Lock company ID',
      lockInactive: 'Unlocked',
      lockSource: 'Lock source',
      lockStartedAt: 'Lock started',
      lockStatus: 'Lock status',
      organizationName: 'Default organization name',
      organizationNameTooltip:
        'When organization sync is not enabled, the top-level organization shows this name.',
      runtimeStatus: 'Runtime status',
      runStatus: {
        abandoned: 'Abandoned',
        failed: 'Failed',
        running: 'Running',
        success: 'Success',
      },
      syncInfo: 'Sync information',
      syncCompanyId: 'Sync company ID',
      syncEnabled: 'Enable organization sync',
      syncSource: 'Sync source',
      tabs: {
        basic: 'Basic info',
        label: 'Organization settings tabs',
        sync: 'Organization sync',
      },
      title: 'Organization settings',
      validation: {
        organizationNameRequired: 'Enter organization name',
      },
    },
    table: {
      actions: 'Actions',
      email: 'Email account',
      name: 'Member name',
      role: 'Role',
      source: 'Source',
    },
    title: 'Departments',
    treeTitle: 'Departments',
    workspaceLabel: 'Departments workspace',
  },
  syncSettings: {
    actions: {
      pullExternalNow: 'Pull external data now',
      pullingExternal: 'Pulling external data',
      saveSettings: 'Save settings',
      syncToHubNow: 'Sync to Hub now',
    },
    description: 'Manage external data pulls, Hub organization sync, and run records separately.',
    external: {
      cronError: 'Enter a six-field Cron expression.',
      cronHint: 'Example: 0 0 18 * * *',
      cronTooltip: 'Authz uses six-field Cron including seconds and runs in UTC.',
      loadErrorDescription:
        'Unable to read the authz sync configuration. Check its URL and status.',
      manualEyebrow: 'External source → Authz shared source tables',
      manualTitle: 'Manual data pull',
      manualTooltip:
        'Ask authz to pull the latest organization data and write accounts, org_units, and user_org_relations. This does not refresh the Hub organization view.',
      pipeline: 'External source → Authz shared source tables',
      settingsTitle: 'External data sync settings',
      settingsTooltip:
        'Authz owns and runs the scheduled sync. It only refreshes shared source tables and does not update Hub organization data.',
      uninitializedNotice:
        'Authz returned no sync configuration. Confirm the enabled state and Cron, then save to initialize it.',
      uninitializedTitle: 'Sync settings are not initialized',
    },
    feedback: {
      externalNotInitialized:
        'Authz has no runnable sync configuration. Initialize the sync settings first.',
      externalCompleted: 'External data pull completed',
      externalRunFailed: 'Failed to start the external data pull',
      externalResultFailed: 'External data pull failed',
      externalResultTimeout:
        'Authz has not returned a final result yet. Check the run status again later.',
      externalSaveFailed: 'Failed to save external data sync settings',
      externalSaved: 'External data sync settings saved',
      externalSubmitted: 'Pull submitted. Waiting for authz to return the result',
      systemCompleted: 'Hub organization data sync completed',
      systemRunFailed: 'Hub organization data sync failed',
      systemSaveFailed: 'Failed to save system data sync settings',
      systemSaved: 'System data sync settings saved',
    },
    fields: {
      cron: 'Cron expression',
      enabled: 'Enable scheduled sync',
    },
    history: {
      columns: {
        duration: 'Duration',
        message: 'Run message',
        result: 'Result',
        scope: 'Scope',
        startedAt: 'Started',
        status: 'Status',
      },
      empty: 'No Hub data sync records',
      externalLatestTitle: 'Latest external data sync status',
      externalLatestTooltip:
        'The current authz API exposes only its latest status, not a full history list, so this section does not imply otherwise.',
      loadErrorDescription: 'Unable to load Hub data sync records. Try again later.',
      resultCounts: '{{units}} organizations / {{relations}} member relations',
      systemRunsTitle: 'Hub data sync records',
      systemRunsTooltip:
        'These records cover sync from shared source tables into the Hub organization view. They do not mean external data was fetched again.',
    },
    loadError: 'Unable to load sync settings',
    result: {
      accounts: 'Accounts',
      emptyExternal:
        'Authz did not return structured statistics. Upgrade authz and complete one successful sync to populate them.',
      externalDelta: 'Created {{created}} · updated {{updated}} · disabled {{disabled}}',
      orgUnits: 'Organization units',
      relationDelta: 'Created {{created}} · updated {{updated}} · removed {{removed}}',
      relations: 'Member relations',
      skipped: 'Skipped data',
      skippedDetail: 'Organizations {{units}} · relations {{relations}}',
      synced: 'Synced successfully in this run',
      syncedOrgUnits: 'Synced organizations',
      syncedRelations: 'Synced member relations',
      title: 'Last sync result',
      tooltip:
        'External sync shows snapshot totals and business deltas; system sync shows Hub write and skipped counts.',
      warnings: '{{count}} data warnings were recorded for this sync',
    },
    status: {
      label: 'Run status',
      lastSyncTime: 'Last run',
      message: 'Run message',
      values: {
        abandoned: 'Abandoned',
        failed: 'Failed',
        running: 'Running',
        success: 'Success',
        unknown: 'No status',
      },
    },
    system: {
      cronError: 'Enter a five-field Cron expression.',
      cronHint: 'Example: 0 2 * * *',
      cronTooltip: 'Hub uses five-field Cron and runs in the Asia/Shanghai timezone.',
      loadErrorDescription: 'Unable to load Hub organization data sync settings.',
      manualEyebrow: 'Shared source tables → Hub organization view',
      manualTitle: 'Manual Hub sync',
      manualTooltip:
        'Read the current shared source tables and refresh the Hub organization view. This does not request any external source.',
      pipeline: 'Shared source tables → Hub organization view',
      settingsTitle: 'System data sync settings',
      settingsTooltip:
        'Hub owns and runs the scheduled sync. It writes existing shared source tables into Hub organization tables and does not pull external data.',
      scope: 'Sync scope',
      scopeOptions: {
        all: 'All organizations',
        selected: 'Selected organizations',
      },
      targets: 'Organizations',
      targetsEmpty: 'No organizations are available in the shared source tables',
      targetsLoadError: 'Unable to load available organizations.',
      targetsRequired: 'Select at least one organization.',
    },
    tabs: {
      external: 'External data sync',
      history: 'Run records',
      label: 'Sync management tabs',
      system: 'System data sync',
    },
    title: 'Sync settings',
  },
  pagination: {
    next: 'Next',
    pageSize: '{{count}} / page',
    previous: 'Previous',
  },
  success: {
    addMember: 'Member added to department',
    batchDisable: 'Members disabled',
    createDepartment: 'Department created',
    createMemberAccount: 'Member account created',
    deleteDepartment: 'Department deleted',
    removeMember: 'Member removed from department',
    renameDepartment: 'Department updated',
    resetPassword: 'Password reset',
    updateSettings: 'Organization settings updated',
    updateMemberAccount: 'Member account updated',
    updateStatus: 'Member status updated',
  },
  temporaryPassword: {
    account: 'Account',
    close: 'Close',
    copied: 'Copied',
    copy: 'Copy',
    copyAccount: 'Copy account',
    copyFailed: 'Copy failed. Copy manually instead.',
    copyPassword: 'Copy password',
    password: 'Password',
    resetHelp:
      'Old sessions are signed out. First sign-in requires a password change. This appears once.',
    resetTitle: 'Password reset',
    title: 'Account created',
  },
}

export default adminOrganization
